import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import {
  TABLE_SORTER_TOOLTIP,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty } from '../../../shared/ui/quiet-states.js';
// Platform admin users screen (archetype: DataView, cf. ContactsPage).
// Differences from tenant screens: no primary create action (users register
// themselves), server-side pagination, client-side status filter for the
// current page, and row actions that audit every mutation.
import { useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnType, TablePaginationConfig } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { useAdminUsers, useAdminUserDetail } from '../hooks/useAdminUsers.js';
import { useAdminUsersMutations } from '../hooks/useAdminUsersMutations.js';
import { AdminUserActions } from '../components/AdminUserActions.js';
import { AdminUserDrawer } from '../components/AdminUserDrawer.js';
import type { AdminUserListItem } from '../api/admin.client.js';

const { Text } = Typography;

type StatusFilter = 'all' | 'verified' | 'unverified' | 'blocked' | 'admins';

const STATUS_FILTER_OPTIONS: StatusFilter[] = [
  'all',
  'verified',
  'unverified',
  'blocked',
  'admins',
];

const DEFAULT_PAGE_SIZE = 20;

export function AdminUsersPage(): ReactElement {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const usersQuery = useAdminUsers({
    page,
    pageSize,
    ...(activeQuery.trim().length > 0 ? { search: activeQuery.trim() } : {}),
  });
  const detailQuery = useAdminUserDetail(selectedUserId);
  const {
    verifyMutation,
    blockMutation,
    unblockMutation,
    sendResetMutation,
    setPlatformRoleMutation,
  } = useAdminUsersMutations();

  const anyMutationPending =
    verifyMutation.isPending ||
    blockMutation.isPending ||
    unblockMutation.isPending ||
    sendResetMutation.isPending ||
    setPlatformRoleMutation.isPending;

  // Client-side status filter applies to the current page; the email/name
  // search stays server-side (it must cover the whole user table).
  const rows = useMemo(() => {
    const list = usersQuery.data?.users ?? [];
    switch (statusFilter) {
      case 'verified':
        return list.filter((user) => user.emailVerified);
      case 'unverified':
        return list.filter((user) => !user.emailVerified);
      case 'blocked':
        return list.filter((user) => user.isBlocked);
      case 'admins':
        return list.filter((user) => user.platformRole === 'admin');
      default:
        return list;
    }
  }, [usersQuery.data, statusFilter]);

  const total = usersQuery.data?.total ?? 0;

  const columns = useMemo<Array<ColumnType<AdminUserListItem>>>(
    () => [
      {
        title: t('crm.admin.users.col_email'),
        dataIndex: 'email',
        key: 'email',
        width: 240,
        render: (value: string) => (
          <Text copyable={{ text: value }} style={{ fontSize: 12 }} className="font-mono">
            {value}
          </Text>
        ),
      },
      {
        title: t('crm.admin.users.col_name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (value: string) => <Text style={{ fontSize: 12 }}>{value}</Text>,
      },
      {
        title: t('crm.admin.users.col_status'),
        key: 'status',
        width: 170,
        render: (_: unknown, user: AdminUserListItem) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge
              status={user.emailVerified ? 'success' : 'default'}
              text={
                <span style={{ fontSize: 11 }}>
                  {user.emailVerified
                    ? t('crm.admin.users.status_verified')
                    : t('crm.admin.users.status_unverified')}
                </span>
              }
            />
            {user.isBlocked ? (
              <Badge
                status="error"
                text={<span style={{ fontSize: 11 }}>{t('crm.admin.users.status_blocked')}</span>}
              />
            ) : null}
          </div>
        ),
      },
      {
        title: t('crm.admin.users.col_platform_role'),
        key: 'platformRole',
        width: 110,
        render: (_: unknown, user: AdminUserListItem) =>
          user.platformRole === 'admin' ? (
            <EntityStatusChip tone="warning" style={{ marginInlineEnd: 0 }}>
              {t('crm.admin.users.role_admin')}
            </EntityStatusChip>
          ) : (
            <Tag style={{ marginInlineEnd: 0 }}>{t('crm.admin.users.role_user')}</Tag>
          ),
      },
      {
        title: t('crm.admin.users.col_workspaces'),
        dataIndex: 'activeWorkspaces',
        key: 'activeWorkspaces',
        width: 110,
        align: 'right',
        render: (value: number) => (
          <Text className="font-semibold font-mono" style={{ fontSize: 12 }}>
            {value}
          </Text>
        ),
      },
      {
        title: t('crm.admin.users.col_created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        render: (value: string) => (
          <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
            {formatDate(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.users.col_last_activity'),
        dataIndex: 'lastActivityAt',
        key: 'lastActivityAt',
        width: 150,
        render: (value: string | null) => (
          <Tooltip title={value === null ? undefined : formatDateTime(value)}>
            <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
              {formatDateTime(value)}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: t('crm.admin.users.col_actions'),
        key: 'actions',
        width: 190,
        render: (_: unknown, user: AdminUserListItem) => (
          <AdminUserActions
            user={user}
            pending={anyMutationPending}
            onVerify={(userId) => verifyMutation.mutateAsync(userId)}
            onBlock={(userId, reason) =>
              blockMutation.mutateAsync(reason === undefined ? { userId } : { userId, reason })
            }
            onUnblock={(userId) => unblockMutation.mutateAsync(userId)}
            onSendReset={(userId) => sendResetMutation.mutateAsync(userId)}
            onSetRole={(userId, role) => setPlatformRoleMutation.mutateAsync({ userId, role })}
          />
        ),
      },
    ],
    [
      t,
      anyMutationPending,
      verifyMutation,
      blockMutation,
      unblockMutation,
      sendResetMutation,
      setPlatformRoleMutation,
    ],
  );

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: [20, 30, 50].map(String),
      onChange: (nextPage, nextPageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
      },
    }),
    [page, pageSize, total],
  );

  function applySearch(value: string): void {
    setActiveQuery(value);
    setPage(1);
  }

  if (usersQuery.isError) {
    return (
      <ErrorState
        title={t('crm.admin.users.load_error')}
        error={usersQuery.error}
        onRetry={() => void usersQuery.refetch()}
      />
    );
  }

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={t('crm.admin.users.title')}
        subtitle={t('crm.admin.users.subtitle', {
          visible: rows.length,
          total,
        })}
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void usersQuery.refetch()}
            loading={usersQuery.isFetching}
            aria-label={t('crm.admin.users.reload')}
            style={LIST_CREATE_BUTTON_STYLE}
          >
            {t('crm.admin.users.reload')}
          </Button>
        }
      />

      <div className="flex flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              aria-label={t('crm.admin.users.search_label')}
              placeholder={t('crm.admin.users.search_placeholder')}
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onPressEnter={applySearch}
            />
          }
        >
          <FilterSelect
            aria-label={t('crm.admin.users.status_filter_label')}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as StatusFilter);
              setPage(1);
            }}
            options={STATUS_FILTER_OPTIONS.map((option) => ({
              value: option,
              label: t(`crm.admin.users.filter_${option}`),
            }))}
          />
        </FilterBar>

        <div style={LIST_SURFACE_STYLE}>
          <Table<AdminUserListItem>
            showSorterTooltip={TABLE_SORTER_TOOLTIP}
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={usersQuery.isLoading}
            pagination={pagination}
            onRow={(user) => ({
              onClick: () => setSelectedUserId(user.id),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <QuietEmpty
                  text={
                    activeQuery.trim().length > 0
                      ? t('crm.admin.users.empty_search')
                      : t('crm.admin.users.empty')
                  }
                />
              ),
            }}
          />
        </div>

        <AdminUserDrawer
          open={selectedUserId !== null}
          userId={selectedUserId}
          detail={detailQuery.data}
          isLoading={detailQuery.isLoading}
          onClose={() => setSelectedUserId(null)}
        />
      </div>
    </div>
  );
}
